import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, Input, PasswordInput, Checkbox, Button } from '../components/ui';
import Layout from '../components/Layout';
import Header from '../components/Header';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  // TODO(X-login): X (Twitter) API の課金が発生する可能性があるため、Xログインを一時的に非表示にしている。
  // 再開する場合は loginWithTwitter の取得・handleTwitterLogin・「Xで続ける」ボタンのコメントアウトを戻す。
  const { signup, login, loginWithGoogle, loginWithDiscord } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // 新規登録時のバリデーション
    if (isSignup) {
      if (password !== confirmPassword) {
        setError('パスワードが一致しません');
        return;
      }
      if (!agreeToTerms) {
        setError('利用規約とプライバシーポリシーに同意してください');
        return;
      }
    }

    setLoading(true);

    try {
      if (isSignup) {
        await signup(email, password);
        // メール確認が済むまでログインできないため、ダッシュボードへは遷移せずこの画面で待機させる
        // （遷移すると未確認のままログイン画面へ戻され、表示と実挙動が食い違う）。
        setSuccess('確認メールを送信しました。メール内のリンクを開いて登録を完了してください。');
      } else {
        await login(email, password);
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Authentication error:', err);
      const errorMessage = err.message?.includes('User already registered')
        ? 'このメールアドレスは既に登録されています。ログインしてください。'
        : err.message?.includes('Email not confirmed')
        ? 'メールアドレスの確認が完了していません。登録時に送信された確認メールを開いてからログインしてください。'
        : err.message?.includes('Invalid login credentials')
        ? 'メールアドレスまたはパスワードが正しくありません'
        : err.message?.includes('Password should be at least 6 characters')
        ? 'パスワードは6文字以上である必要があります'
        : err.message?.includes('Unable to validate email address')
        ? 'メールアドレスの形式が正しくありません'
        : err.message?.includes('email rate limit exceeded')
        ? 'メールの送信が制限されています。しばらく時間をおいてから再度お試しください。'
        : err.message || '認証に失敗しました';
      setError(errorMessage);

      // 既に登録されている場合はログインモードに切り替え
      if (err.message?.includes('User already registered')) {
        setIsSignup(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      await loginWithGoogle();
      // リダイレクトするので navigate は不要
    } catch (err: any) {
      console.error('Google login error:', err);
      setError('Googleログインに失敗しました');
      setLoading(false);
    }
  };

  // TODO(X-login): 一時的に非表示中。再開時にコメントアウトを解除する。
  // const handleTwitterLogin = async () => {
  //   setError('');
  //   setLoading(true);
  //   try {
  //     await loginWithTwitter();
  //     // リダイレクトするので navigate は不要
  //   } catch (err: any) {
  //     console.error('X login error:', err);
  //     setError('Xログインに失敗しました');
  //     setLoading(false);
  //   }
  // };

  const handleDiscordLogin = async () => {
    setError('');
    setLoading(true);
    try {
      await loginWithDiscord();
      // リダイレクトするので navigate は不要
    } catch (err: any) {
      console.error('Discord login error:', err);
      setError('Discordログインに失敗しました');
      setLoading(false);
    }
  };

  const switchMode = () => {
    setIsSignup(!isSignup);
    setError('');
    setSuccess('');
    setConfirmPassword('');
    setAgreeToTerms(false);
  };

  // エラー文言から該当フィールドを導出し、aria-invalid / danger ボーダーを提示する（表示のみ・送信ロジックは不変）。
  // 「メールアドレスまたはパスワードが正しくありません」のように両方を含む資格情報エラーは原因が一意でないため、
  // フィールド個別の error にはせず role=alert バナーのみで提示する。
  const mentionsEmail = error.includes('メールアドレス');
  const mentionsPassword = error.includes('パスワード');
  const isAmbiguousCredentialError = mentionsEmail && mentionsPassword;
  const emailError = mentionsEmail && !isAmbiguousCredentialError;
  const passwordError = mentionsPassword && !isAmbiguousCredentialError;

  return (
    <Layout>
      <Header />

      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <Card className="max-w-md w-full">
          {/* タイトル部分 - モードで切り替え */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-text">
              {isSignup ? '新規アカウント作成' : 'ログイン'}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {isSignup
                ? 'アカウントを作成してotomochiを始めましょう'
                : 'アカウントにログインしてください'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium mb-2">メールアドレス</label>
              <Input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={emailError}
                aria-describedby={error ? 'login-error' : undefined}
                placeholder="example@email.com"
                required
              />
            </div>

            <div>
              <label htmlFor="login-password" className="block text-sm font-medium mb-2">パスワード</label>
              <PasswordInput
                id="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={passwordError}
                aria-describedby={error ? 'login-error' : undefined}
                placeholder={isSignup ? '6文字以上で入力' : 'パスワードを入力'}
                autoComplete={isSignup ? 'new-password' : 'current-password'}
                minLength={6}
                required
              />
            </div>

            {/* 新規登録時のみ表示 - パスワード確認 */}
            {isSignup && (
              <div>
                <label htmlFor="login-confirm" className="block text-sm font-medium mb-2">パスワード（確認）</label>
                <PasswordInput
                  id="login-confirm"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={passwordError}
                  aria-describedby={error ? 'login-error' : undefined}
                  placeholder="パスワードを再入力"
                  autoComplete="new-password"
                  minLength={6}
                  required
                />
              </div>
            )}

            {/* 新規登録時のみ表示 - 利用規約同意 */}
            {isSignup && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-700">
                    <Link to="/terms" target="_blank" className="text-primary hover:underline">利用規約</Link>
                    および
                    <Link to="/privacy" target="_blank" className="text-primary hover:underline">プライバシーポリシー</Link>
                    に同意します
                  </span>
                </label>
              </div>
            )}

            {error && (
              <div id="login-error" role="alert" className="bg-danger-bg text-danger-fg px-4 py-3 rounded-field text-sm">
                {error}
              </div>
            )}

            {success && (
              <div role="status" className="bg-success-bg text-success-fg px-4 py-3 rounded-field text-sm">
                {success}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              loading={loading}
              className="w-full"
              disabled={loading || (isSignup && !agreeToTerms)}
            >
              {loading ? '処理中...' : isSignup ? 'アカウントを作成' : 'ログイン'}
            </Button>
          </form>

          {/* モード切り替え */}
          <div className="mt-6 pt-6 border-t text-center">
            <p className="text-sm text-gray-600">
              {isSignup ? '既にアカウントをお持ちですか？' : 'アカウントをお持ちでないですか？'}
            </p>
            <button
              type="button"
              className="mt-2 text-primary font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded"
              onClick={switchMode}
            >
              {isSignup ? 'ログインはこちら' : '新規登録はこちら'}
            </button>
          </div>

          {/* ソーシャルログイン */}
          <div className="mt-6 pt-6 border-t">
            <p className="text-sm text-gray-600 text-center mb-4">
              または
            </p>
            <div className="space-y-2">
              <Button
                variant="secondary"
                onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center space-x-2"
                disabled={loading}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Googleで続ける</span>
              </Button>
              {/* TODO(X-login): X (Twitter) API の課金が発生する可能性があるため一時的に非表示。再開時にコメントアウトを解除する。
              <Button
                variant="secondary"
                onClick={handleTwitterLogin}
                className="w-full flex items-center justify-center space-x-2"
                disabled={loading}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                <span>Xで続ける</span>
              </Button>
              */}
              <Button
                variant="secondary"
                onClick={handleDiscordLogin}
                className="w-full flex items-center justify-center space-x-2"
                disabled={loading}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#5865F2">
                  <path d="M20.317 4.369A19.79 19.79 0 0 0 15.432 3a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.249a18.27 18.27 0 0 0-5.487 0 12.6 12.6 0 0 0-.617-1.25.077.077 0 0 0-.079-.036A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
                <span>Discordで続ける</span>
              </Button>
            </div>
            <p className="text-xs text-gray-500 text-center mt-4">
              ソーシャルログインを使用すると、
              <Link to="/terms" className="text-primary hover:underline">利用規約</Link>
              と
              <Link to="/privacy" className="text-primary hover:underline">プライバシーポリシー</Link>
              に同意したことになります
            </p>
          </div>
        </Card>
      </main>
    </Layout>
  );
}
